import { Controller } from 'react-hook-form';
import { Field } from '../../../forms/fields';
import type { SectionProps } from '../sectionProps';

/**
 * ORDER editor (PLAN.md M7 Strategy — contribution / withdrawal order). Edits an ordered list of
 * ACCOUNT IDS: the simulator funnels each year's surplus into accounts in `contributionOrder` and
 * draws each shortfall from accounts in `withdrawalOrder`. Renders the included accounts as a
 * reorderable list (up/down), with a way to include/exclude an account (an empty list means
 * "natural order"). Up/down avoids a drag-and-drop dependency while staying keyboard-accessible.
 */

export interface OrderEditorProps {
  form: SectionProps['form'];
  name: 'strategy.contributionOrder' | 'strategy.withdrawalOrder';
  label: string;
  /** All available accounts: { id, name } for labels + the "add" picker. */
  accounts: ReadonlyArray<{ id: string; name: string }>;
  hint?: string | undefined;
}

export function OrderEditor({
  form,
  name,
  label,
  accounts,
  hint,
}: OrderEditorProps): React.ReactElement {
  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => {
        const order = (field.value as string[] | undefined) ?? [];
        const labelFor = (id: string): string => {
          const a = accounts.find((x) => x.id === id);
          return a !== undefined && a.name.length > 0 ? `${a.name} (${id})` : id;
        };
        const remaining = accounts.filter((a) => !order.includes(a.id));

        const move = (index: number, delta: number): void => {
          const next = [...order];
          const target = index + delta;
          if (target < 0 || target >= next.length) return;
          const moved = next[index];
          const swapped = next[target];
          if (moved === undefined || swapped === undefined) return;
          next[index] = swapped;
          next[target] = moved;
          field.onChange(next);
        };
        const removeAt = (index: number): void => {
          field.onChange(order.filter((_, i) => i !== index));
        };
        const add = (id: string): void => {
          if (id === '' || order.includes(id)) return;
          field.onChange([...order, id]);
        };

        return (
          <Field label={label} hint={hint}>
            <ol className="order">
              {order.length === 0 && (
                <li className="order__empty">Natural account order (none specified).</li>
              )}
              {order.map((id, i) => (
                <li key={id} className="order__row">
                  <span className="order__index num">{i + 1}</span>
                  <span className="order__label">{labelFor(id)}</span>
                  <span className="order__controls">
                    <button
                      type="button"
                      className="btn btn--ghost order__btn"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      aria-label={`Move ${labelFor(id)} up`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost order__btn"
                      onClick={() => move(i, 1)}
                      disabled={i === order.length - 1}
                      aria-label={`Move ${labelFor(id)} down`}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost order__btn"
                      onClick={() => removeAt(i)}
                      aria-label={`Remove ${labelFor(id)}`}
                    >
                      ✕
                    </button>
                  </span>
                </li>
              ))}
            </ol>
            {remaining.length > 0 && (
              <select
                className="select"
                style={{ maxWidth: 260, marginTop: 'var(--sp-2)' }}
                value=""
                aria-label={`Add account to ${label}`}
                onChange={(e) => {
                  add(e.currentTarget.value);
                  e.currentTarget.value = '';
                }}
              >
                <option value="">+ Add account…</option>
                {remaining.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name.length > 0 ? `${a.name} (${a.id})` : a.id}
                  </option>
                ))}
              </select>
            )}
          </Field>
        );
      }}
    />
  );
}
